import React, { createContext, useMemo, useState } from 'react'
import { useScriptLoader } from 'hooks'
import config from 'config'
import logger from 'logger'
import type { RecurlyOptions } from '@recurly/recurly-js'

import { ErrorBlock } from 'components/feedback'
import { Bone } from 'components/layout'

import PageSkeleton from 'compositions/PageSkeleton/PageSkeleton'
import PageError from 'compositions/PageError/PageError'


export const RecurlyContext = createContext<PaymentModule.RecurlyContext>(null)

const useRecurlyLoader = () => {
  const { isScriptLoaded, scriptLoadingError } = useScriptLoader('https://js.recurly.com/v4/recurly.js')
  const [ isReady, setIsReady ] = useState(false)

  const recurlyContext = useMemo<PaymentModule.RecurlyContext>(() => {
    if (scriptLoadingError) {
      logger.error(scriptLoadingError, '[Recurly] Can\'t load recurly script')
    }

    if (!isScriptLoaded) {
      return
    }

    // @ts-ignore
    logger.info(`[Recurly] Recurly script loaded, version: ${window.recurly.version}`)

    const recurly = window.recurly

    try {
      const fraud: RecurlyOptions['fraud'] = {}

      // we reset fraud to make sure that fraud session id is added
      // @ts-ignore
      if (recurly.fraud) {
        // @ts-ignore
        recurly.fraud.destroy()
        // @ts-ignore
        recurly.fraud = null
      }

      fraud.kount = {
        dataCollector: true,
        // @ts-ignore
        form: '[data-testid="paymentForm"]',
      }

      recurly.configure({
        publicKey: config.recurlyPublicKey,
        fraud,
      })

      // @ts-ignore
      recurly.fraud?.on('ready', () => {
        logger.info('[Recurly] fraud ready')
      })

      recurly.on('error', (error) => {
        logger.warn(error, `[Recurly] ${error.message}`)
      })

      // @ts-ignore
      recurly.ready(() => {
        logger.info(`[Recurly] Ready`)

        setIsReady(true)
      })
    }
    catch (error) {
      logger.error(error)
      return null
    }

    logger.info('[Recurly] Create Elements')

    const elements = recurly.Elements()

    return {
      recurly,
      elements,
    }
  }, [ isScriptLoaded, scriptLoadingError ])

  return {
    recurlyContext: isReady ? recurlyContext : null,
    error: scriptLoadingError,
  }
}


const RecurlyProvider: React.CFC<{ withLayout?: boolean }> = ({ children, withLayout = true }) => {
  const { recurlyContext, error } = useRecurlyLoader()

  if (error) {
    if (withLayout) {
      return (
        <PageError />
      )
    }

    return (
      <ErrorBlock />
    )
  }

  if (!recurlyContext) {
    if (withLayout) {
      return (
        <PageSkeleton />
      )
    }

    // TODO implement loader prop if it's needed - added on 2021-08-07 by maddoger
    return (
      <Bone pw={100} h={100} />
    )
  }

  return (
    <RecurlyContext.Provider value={recurlyContext}>
      {children}
    </RecurlyContext.Provider>
  )
}


export default RecurlyProvider
